import {
  AutoComplete,
  DatePicker,
  InputNumber,
  Modal,
  Table,
  notification,
} from "antd";
import {
  Button,
  Checkbox,
  Input,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import {
  IconCalculator,
  IconCashBanknote,
  IconBuilding,
  IconCalendar,
  IconChevronDown,
  IconFileExport,
  IconFileText,
  IconFolderOpen,
  IconHash,
  IconMail,
  IconMessage,
  IconPercentage,
  IconPlus,
  IconReceipt,
  IconSearch,
  IconShield,
  IconShieldCheck,
  IconTicket,
  IconTrash,
  IconUsers,
  IconWallet,
} from "@tabler/icons-react";
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

const numberFormatter = (value: string | number | undefined) =>
  `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const numberParser = (value: string | undefined) =>
  (value || "").replace(/,/g, "");
const formatDecimal = (value: number | string | undefined | null) => {
  const n = Math.round(Number(value) || 0);
  return (n === 0 ? 0 : n).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
};

interface ReceivableDetailRecord {
  id?: string;
  key?: string;
  paymentDate?: string | null;
  amount?: number | string;
  bankId?: string;
  paymentMethod?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  remarks?: string;
  checkNumber?: string;
  checkClearanceDate?: string | null;
  handlingFee?: number;
  retentionAmount?: number;
  deductionAmount?: number;
  isSettled?: boolean;
}

function AccountReceivableMainPage() {
  const { t } = useTranslation();
  const [accountReceivable, setAccountReceivable] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({
    name: "",
    requestedAmount: 0,
    taxRate: 0,
    partnerId: "",
    remarks: "",
  });
  const [tempReceivableList, setTempReceivableList] = useState<ReceivableDetailRecord[]>([]);
  const [options, setOptions] = useState([]);
  const [partnerData, setPartnerData] = useState([]);
  const [bankData, setBankData] = useState([]);
  const [tempPartnerInput, SetTempPartnerInput] = useState("");
  const [displayAccountReceivable, setDisplayAccountReceivable] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [exportDateRange, setExportDateRange] = useState<any>(null);
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(true);
  const [performanceBond, setPerformanceBond] = useState({ checkNumber: "", retrievalDate: null as string | null, isRetrieved: false });
  const [engineeringInsurance, setEngineeringInsurance] = useState({ startDate: null as string | null, expiryDate: null as string | null });
  const [warrantyBond, setWarrantyBond] = useState({ checkNumber: "", retrievalDate: null as string | null, isRetrieved: false });
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();

  const filteredAccountReceivable = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return displayAccountReceivable;
    return displayAccountReceivable.filter((row) => {
      const partner = row?.partnerId
        ? partnerData.find((p) => p.id === row.partnerId)
        : null;
      return [row?.name, partner?.name, row?.address, row?.remarks]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [displayAccountReceivable, searchText, partnerData]);

  const selectedSummary = useMemo(() => {
    const selectedRows = displayAccountReceivable.filter((row) =>
      selectedRowKeys.includes(row.key)
    );
    return selectedRows.reduce(
      (acc, row) => {
        const totalWithTax =
          (row?.requestedAmount || 0) +
          (row?.requestedAmount || 0) * ((row?.taxRate || 0) / 100);
        const paid =
          row?.accountsReceivableDetail?.reduce(
            (s, t) =>
              s + (isNaN(parseFloat(t.amount)) ? 0 : parseFloat(t.amount)),
            0
          ) || 0;
        acc.totalAmount += totalWithTax;
        acc.paidAmount += paid;
        acc.unpaidAmount += totalWithTax - paid;
        return acc;
      },
      { totalAmount: 0, paidAmount: 0, unpaidAmount: 0 }
    );
  }, [displayAccountReceivable, selectedRowKeys]);

  const handleChangePartnerData = (value, data, dataType) => {
    let match = value.match(/ID:\s*([a-zA-Z0-9-]+)/);
    let idNumber = match ? match[1] : null;
    let selectedData = data.filter((e) => e?.id == idNumber)[0];
    if (selectedData) {
      SetTempPartnerInput(selectedData.name + ` (ID: ${selectedData.id})`);
      handleChangeData(selectedData.id, dataType);
    }
  };

  const handleChangeData = (value, type) => {
    setAddFormData((prev) => ({ ...prev, [type]: value }));
  };

  const resetAddModalState = () => {
    setAddFormData({
      name: "",
      requestedAmount: 0,
      taxRate: 0,
      partnerId: "",
      remarks: "",
    });
    setTempReceivableList([]);
    SetTempPartnerInput("");
    setPerformanceBond({ checkNumber: "", retrievalDate: null, isRetrieved: false });
    setEngineeringInsurance({ startDate: null, expiryDate: null });
    setWarrantyBond({ checkNumber: "", retrievalDate: null, isRetrieved: false });
    setExportDateRange(null);
  };

  const openAddModal = () => {
    resetAddModalState();
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    resetAddModalState();
  };

  const columns = [
    {
      title: "",
      dataIndex: "key",
      rowScope: "row",
    },
    {
      title: t("accountReceivable.name") || "名稱",
      rowSpan: 3,
      dataIndex: "name",
    },
    {
      title: t("accountReceivable.totalAmountWithTax") || "總金額（含稅)",
      render: (val) => {
        const total =
          (val?.requestedAmount || 0) +
          (val?.requestedAmount || 0) * ((val?.taxRate || 0) / 100);
        return <div>{formatDecimal(total)}</div>;
      },
    },
    {
      title: t("accountReceivable.unpaidAmount") || "未收金額",
      render: (val) => {
        const paid = val?.accountsReceivableDetail?.reduce(
          (acc, t) => acc + (isNaN(parseFloat(t.amount)) ? 0 : parseFloat(t.amount)),
          0
        ) || 0;
        const totalWithTax =
          (val?.requestedAmount || 0) +
          (val?.requestedAmount || 0) * ((val?.taxRate || 0) / 100);
        return <div>{formatDecimal(totalWithTax - paid)}</div>;
      },
    },
    {
      title: t("accountReceivable.paidAmount") || "已收金額",
      render: (val) => {
        const paid = val?.accountsReceivableDetail?.reduce(
          (acc, t) => acc + (isNaN(parseFloat(t.amount)) ? 0 : parseFloat(t.amount)),
          0
        ) || 0;
        return <div>{formatDecimal(paid)}</div>;
      },
    },
    {
      title: t("accountReceivable.totalRetentionAmount") || "保留款總計",
      render: (val) => {
        const totalRetention = val?.accountsReceivableDetail?.reduce(
          (acc, t) => acc + (Number(t.retentionAmount) || 0),
          0
        ) || 0;
        return <div>{formatDecimal(totalRetention)}</div>;
      },
    },
    {
      title: t("accountReceivable.inProgressAmount") || "進行中金額",
      render: (val) => {
        const inProgress = val?.accountsReceivableDetail?.reduce((acc, t) => {
          if (t.isSettled) return acc;
          const amount = isNaN(parseFloat(t.amount)) ? 0 : parseFloat(t.amount);
          const retention = Number(t.retentionAmount) || 0;
          return acc + (amount - retention);
        }, 0) || 0;
        return <div>{formatDecimal(inProgress)}</div>;
      },
    },
    {
      title: t("accountReceivable.partner") || "合作方",
      render: (val) => {
        const partner = val?.partnerId ? getPartnerById(val.partnerId) : null;
        return partner ? <div>{partner.name}</div> : "N/A";
      },
    },
    {
      title: t("accountReceivable.project") || "專案",
      dataIndex: "address",
    },
    {
      title: t("accountReceivable.remarks") || "備註",
      dataIndex: "remarks",
    },
    {
      title: "",
      fixed: "right",
      render: ({ key }) => {
        return (
          <>
            <div>
              <Button
                variant="default"
                style={{ margin: "0.3rem" }}
                radius="md"
                onClick={() => {
                  const accountReceivableId =
                    displayAccountReceivable[key - 1]?.id;
                  if (accountReceivableId) {
                    navigate(
                      `/hrMainPage/editAccountReceivable/${accountReceivableId}`
                    );
                  }
                }}
              >
                {t("accountReceivable.edit") || "編輯"}
              </Button>

              <Button
                variant="filled"
                color="gray"
                style={{ margin: "0.3rem" }}
                radius="md"
                onClick={() => {
                  const accountReceivable = displayAccountReceivable[key - 1];

                  Modal.confirm({
                    title: t("accountReceivable.confirmDelete") || "確認刪除",
                    content: (
                      <div>
                        <p>{t("accountReceivable.confirmDeleteContent") || "確定要刪除此應收帳款嗎？此操作無法復原。"}</p>
                        <div style={{ marginTop: "10px", color: "#666" }}></div>
                      </div>
                    ),
                    okText: t("accountReceivable.confirm") || "確認",
                    cancelText: t("accountReceivable.cancel") || "取消",
                    okButtonProps: { danger: true },
                    onOk: async () => {
                      try {
                        await single(
                          orpcClient.accountsReceivable.delete([
                            { id: accountReceivable?.id },
                          ])
                        );
                        fetchReceivableData();
                        api.success({
                          message: t("accountReceivable.deleteSuccess") || "刪除成功",
                          description: t("accountReceivable.deleteSuccessDesc") || "應收帳款已成功刪除",
                          placement: "topLeft",
                        });
                      } catch (error) {
                        api.error({
                          message: t("accountReceivable.deleteFailed") || "刪除失敗",
                          description: (error as any)?.message || t("accountReceivable.deleteFailed") || "刪除失敗",
                          placement: "topLeft",
                        });
                      }
                    },
                  });
                }}
              >
                {t("accountReceivable.delete") || "刪除"}
              </Button>
            </div>
          </>
        );
      },
    },
  ];

  const handleChangeReceivableDetail = (id, value, type) => {
    setTempReceivableList((prev) => {
      const updatedList = prev.map((receivable) => {
        if (receivable.id === id || receivable?.key === id) {
          return {
            ...receivable,
            [type]: value,
          };
        }
        return receivable;
      });
      return updatedList;
    });
  };

  const handleGetPartnerData = (partnerData, text) => {
    let result = partnerData
      ?.map((t) => ({
        label: t?.name + ` (ID: ${t?.id})`,
        value: t?.name + ` (ID: ${t?.id})`,
      }))
      ?.filter((item) => item.label.toLowerCase().includes(text.toLowerCase()));
    return result;
  };

  const getPartnerById = (partnerId) => {
    return partnerData.find((partner) => partner.id === partnerId);
  };

  const getBankById = (bankId) => {
    return bankData.find((bank) => bank.id === bankId);
  };

  const fetchReceivableData = async () => {
    try {
      let result = await orpcClient.accountsReceivable.list({});
      setAccountReceivable(result);
      const updatedData = result
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .map((t, i) => ({
          ...t,
          key: i + 1,
        }));
      setDisplayAccountReceivable(updatedData);
    } catch (error) {
      console.error("Error fetching accounts receivable data:", error);
    }
  };

  useEffect(() => {
    fetchReceivableData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.partner.list({});
        setPartnerData(result);
      } catch (error) {
        console.error("Error fetching partner data:", error);
      }
    };

    const fetchBankData = async () => {
      try {
        let result = await orpcClient.bank.list({});
        setBankData(result);
      } catch (error) {
        console.error("Error fetching bank data:", error);
      }
    };

    fetchData();
    fetchBankData();
  }, []);

  const handleExportModalRecords = () => {
    let recordsToExport = tempReceivableList;
    if (exportDateRange && exportDateRange[0] && exportDateRange[1]) {
      recordsToExport = tempReceivableList.filter((record) => {
        if (!record.paymentDate) return false;
        const date = dayjs(record.paymentDate);
        return (
          (date.isAfter(exportDateRange[0], "day") || date.isSame(exportDateRange[0], "day")) &&
          (date.isBefore(exportDateRange[1], "day") || date.isSame(exportDateRange[1], "day"))
        );
      });
    }
    const exportData = recordsToExport.map((record) => {
      const bank = record?.bankId ? getBankById(record.bankId) : null;
      return {
        [t("accountReceivable.collectionDate") || "收款日期"]: record.paymentDate || "",
        [t("accountReceivable.collectionAmount") || "收款金額"]: formatDecimal(record.amount || 0),
        [t("accountReceivable.bank") || "銀行"]: bank ? bank.name : "",
        [t("accountReceivable.paymentMethod") || "付款方式"]: record.paymentMethod || "",
        [t("accountReceivable.invoiceNumber") || "發票號碼"]: record.invoiceNumber || "",
        [t("accountReceivable.invoiceDate") || "發票日期"]: record.invoiceDate || "",
        [t("accountReceivable.remarks") || "備註"]: record.remarks || "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("accountReceivable.collectionRecord") || "收款紀錄");
    XLSX.writeFile(wb, `collection_records_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportExcel = () => {
    const selectedData = displayAccountReceivable.filter((row) =>
      selectedRowKeys.includes(row.key)
    );
    const exportData = selectedData.map((row) => {
      const partner = row?.partnerId ? getPartnerById(row.partnerId) : null;
      const paidAmount = row?.accountsReceivableDetail?.reduce(
        (acc, t) => acc + (isNaN(parseFloat(t.amount)) ? 0 : parseFloat(t.amount)),
        0
      ) || 0;
      const totalWithTax =
        (row.requestedAmount || 0) +
        (row.requestedAmount || 0) * ((row.taxRate || 0) / 100);
      return {
        [t("accountReceivable.name") || "名稱"]: row.name,
        [t("accountReceivable.totalAmountWithTax") || "總金額（含稅)"]: formatDecimal(totalWithTax),
        [t("accountReceivable.paidAmount") || "已收金額"]: formatDecimal(paidAmount),
        [t("accountReceivable.unpaidAmount") || "未收金額"]: formatDecimal(totalWithTax - paidAmount),
        [t("accountReceivable.partner") || "合作方"]: partner ? partner.name : "",
        [t("accountReceivable.project") || "專案"]: row.address || "",
        [t("accountReceivable.remarks") || "備註"]: row.remarks || "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("accountReceivable.title") || "應收帳款");
    XLSX.writeFile(wb, `accounts_receivable_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="min-h-screen">
      {contextHolder}
      <div
        className="flex items-center justify-between px-4"
        style={{ backgroundColor: "#ECECEC" }}
      >
        <div className="flex flex-1 justify-center">
          <TextInput
            leftSection={<IconSearch size={16} />}
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
            placeholder={t("accountReceivable.searchPlaceholder") || "搜尋名稱、合作方、專案或備註..."}
            style={{ width: "100%", maxWidth: "32rem" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Button
            variant="default"
            style={{ minWidth: "3rem", margin: "1rem 0" }}
            radius="xl"
            leftSection={<IconFileExport size={16} />}
            disabled={selectedRowKeys.length === 0}
            onClick={handleExportExcel}
          >
            {t("accountReceivable.exportExcel") || "匯出Excel"}
          </Button>
          <Button
            variant="default"
            style={{ minWidth: "3rem", margin: "1rem" }}
            radius="xl"
            onClick={openAddModal}
          >
            {t("accountReceivable.addAccountReceivable") || "新增應收帳款"}
          </Button>
        </div>
      </div>

      <div className="rounded-xl bg-white p-8 m-8">
        {selectedRowKeys.length > 0 && (
          <div className="mb-4 grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <IconCashBanknote size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">
                    {t("accountReceivable.totalAmountWithTax") || "總金額（含稅)"}
                    <span className="ml-1 text-gray-400">
                      ({t("accountReceivable.selected") || "已選"} {selectedRowKeys.length})
                    </span>
                  </p>
                  <p className="text-lg font-semibold text-blue-600">
                    {formatDecimal(selectedSummary.totalAmount)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                  <IconCashBanknote size={20} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">
                    {t("accountReceivable.unpaidAmount") || "未收金額"}
                  </p>
                  <p className="text-lg font-semibold text-orange-600">
                    {formatDecimal(selectedSummary.unpaidAmount)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                  <IconReceipt size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">
                    {t("accountReceivable.paidAmount") || "已收金額"}
                  </p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatDecimal(selectedSummary.paidAmount)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        <Table
          scroll={{ x: "max-content" }}
          columns={columns}
          dataSource={filteredAccountReceivable}
          bordered
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
        />
      </div>

      <Modal
        styles={{ padding: "0px", minWidth: "500px", width: "100%" }}
        open={isAddModalOpen}
        width={1000}
        className="p-0"
        title={t("accountReceivable.addAccountReceivable") || "新增應收帳款"}
        onClose={closeAddModal}
        onCancel={closeAddModal}
        okText={t("hrMain.add") || "新增"}
        cancelText={t("accountReceivable.cancel") || "取消"}
        onOk={async () => {
          try {
            for (let i = 0; i < tempReceivableList.length; i++) {
              const record = tempReceivableList[i];
              const missingFields = [];

              if (!record.paymentDate)
                missingFields.push(
                  t("accountReceivable.collectionDate") || "收款日期"
                );
              if (!record.amount && record.amount !== 0)
                missingFields.push(
                  t("accountReceivable.collectionAmount") || "收款金額"
                );
              if (!record.bankId)
                missingFields.push(t("accountReceivable.bank") || "銀行");
              if (!record.invoiceNumber)
                missingFields.push(
                  t("accountReceivable.invoiceNumber") || "發票號碼"
                );
              if (!record.invoiceDate)
                missingFields.push(
                  t("accountReceivable.invoiceDate") || "發票日期"
                );

              if (missingFields.length > 0) {
                api.warning({
                  message: t("accountReceivable.requiredFields") || "請填寫必填欄位",
                  description: (
                    <div>
                      {t("accountReceivable.collectionRecordMissingDesc", { index: i + 1 }) || `收款紀錄 #${i + 1} 缺少以下必填欄位：`}
                      <br />
                      {missingFields.join(t("accountReceivable.listSeparator") || "、")}
                    </div>
                  ),
                  placement: "topLeft",
                  duration: 5,
                });
                return;
              }
            }

            const result = await single(
              orpcClient.accountsReceivable.create([
                {
                  name: addFormData.name,
                  requestedAmount: Math.round(
                    Number(addFormData.requestedAmount) || 0
                  ),
                  taxRate: Math.round(Number(addFormData.taxRate) || 0),
                  partnerId: addFormData.partnerId,
                  remarks: addFormData.remarks || undefined,
                  documentRecords: {
                    performanceBond,
                    engineeringInsurance,
                    warrantyBond,
                  },
                },
              ])
            );

            if (result) {
              if (tempReceivableList.length > 0) {
                const batchResults =
                  await orpcClient.accountsReceivableDetail.create(
                    tempReceivableList.map((data) => ({
                      accountsReceivableId: result?.id,
                      bankId: data?.bankId || "",
                      paymentDate: data?.paymentDate
                        ? new Date(data.paymentDate)
                        : new Date(),
                      amount: Math.round(Number(data?.amount) || 0),
                      invoiceNumber: data?.invoiceNumber || "",
                      invoiceDate: data?.invoiceDate
                        ? new Date(data.invoiceDate)
                        : new Date(),
                      paymentMethod: (data?.paymentMethod ||
                        "transfer") as "check" | "transfer" | "cash" | "credit",
                      remarks: data?.remarks || undefined,
                      checkNumber: data?.checkNumber || undefined,
                      checkClearanceDate: data?.checkClearanceDate
                        ? new Date(data.checkClearanceDate)
                        : undefined,
                      handlingFee: Math.round(Number(data?.handlingFee) || 0),
                      retentionAmount: Math.round(Number(data?.retentionAmount) || 0),
                      deductionAmount: Math.round(Number(data?.deductionAmount) || 0),
                      isSettled: data?.isSettled ?? false,
                    }))
                  );
                if (!batchResults.every((res) => res.success)) {
                  return api.error({
                    message: `Notification `,
                    description: <div>{t("accountReceivable.addDetailFailed") || "新增收款明細失敗"}</div>,
                    placement: "topLeft",
                    duration: 0,
                  });
                }
              }

              closeAddModal();
              fetchReceivableData();
            }
          } catch (error) {
            api.error({
              message: `Notification `,
              description: <div>{(error as any)?.message || "新增應收帳款失敗"}</div>,
              placement: "topLeft",
              duration: 0,
            });
          }
        }}
      >
        <div className="space-y-5 py-2">
          {/* Basic Information */}
          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
              <IconFileText size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">
                {t("accountReceivable.basicInfo") || "基本資訊"}
              </h3>
            </div>
            <div className="grid grid-cols-6 gap-4 p-6">
              <div className="col-span-3 space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  {t("accountReceivable.accountReceivableName") || "應收帳款名稱"}＊
                </label>
                <Input
                  value={addFormData.name}
                  onChange={(e) => handleChangeData(e.target.value, "name")}
                  placeholder={t("accountReceivable.enterName") || "請輸入名稱"}
                  leftSection={<IconMail size={16} />}
                />
              </div>

              <div className="col-span-3 space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <IconUsers size={14} />
                  {t("accountReceivable.partnerSupplier") || "合作方/供應商"}＊
                </label>
                <AutoComplete
                  style={{ width: "100%" }}
                  value={tempPartnerInput || ""}
                  placeholder={t("accountReceivable.enterPartner") || "請輸入合作方名稱"}
                  options={options}
                  onFocus={() =>
                    setOptions(handleGetPartnerData(partnerData, ""))
                  }
                  onSearch={(text) =>
                    setOptions(handleGetPartnerData(partnerData, text))
                  }
                  onChange={(e) => SetTempPartnerInput(e)}
                  onSelect={(e) =>
                    handleChangePartnerData(e, partnerData, "partnerId")
                  }
                />
              </div>

              <div className="col-span-2 space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  {t("accountReceivable.requestedAmount") || "應收金額"}＊
                </label>
                <InputNumber
                  style={{ width: "100%" }}
                  value={addFormData.requestedAmount}
                  onChange={(val) => handleChangeData(val, "requestedAmount")}
                  prefix={<IconCashBanknote size={16} />}
                  formatter={numberFormatter}
                  parser={numberParser}
                  step={0.01}
                />
              </div>

              <div className="col-span-2 space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  {t("accountReceivable.taxRate") || "稅率(%)"}
                </label>
                <InputNumber
                  style={{ width: "100%" }}
                  value={addFormData.taxRate}
                  onChange={(val) => handleChangeData(val, "taxRate")}
                  prefix={<IconPercentage size={16} />}
                  formatter={numberFormatter}
                  parser={numberParser}
                  step={0.01}
                />
              </div>

              <div className="col-span-2 space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  {t("accountReceivable.taxAmount") || "稅額"}
                </label>
                <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
                  {formatDecimal(
                    (Number(addFormData.requestedAmount) || 0) *
                    ((Number(addFormData.taxRate) || 0) / 100)
                  )}
                </div>
              </div>

              <div className="col-span-6 space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  {t("accountReceivable.totalAmountWithTax") || "含稅總金額"}
                </label>
                <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-900">
                  {formatDecimal(
                    (Number(addFormData.requestedAmount) || 0) +
                    (Number(addFormData.requestedAmount) || 0) *
                    ((Number(addFormData.taxRate) || 0) / 100)
                  )}
                </div>
              </div>

              <div className="col-span-6 space-y-1.5">
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <IconMessage size={14} />
                  {t("accountReceivable.remarks") || "備註"}
                </label>
                <Textarea
                  placeholder="請輸入備註事項..."
                  value={addFormData.remarks}
                  onChange={(e) =>
                    handleChangeData(e.target.value, "remarks")
                  }
                  minRows={2}
                  autosize
                />
              </div>
            </div>
          </div>

          {/* Document Records */}
          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <div
              className="flex cursor-pointer items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4 hover:bg-gray-100"
              onClick={() => setIsDocumentsOpen(!isDocumentsOpen)}
            >
              <div className="flex items-center gap-2">
                <IconFolderOpen size={16} className="text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">
                  {t("accountReceivable.documentRecords") || "文件紀錄"}
                </h3>
                <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-500">
                  {[performanceBond.isRetrieved, warrantyBond.isRetrieved].filter(Boolean).length} / 2 {t("accountReceivable.retrieved") || "已取回"}
                </span>
              </div>
              <IconChevronDown
                size={16}
                className={`text-gray-400 transition-transform duration-200 ${isDocumentsOpen ? "rotate-180" : ""}`}
              />
            </div>

            {isDocumentsOpen && (
              <div>
                {/* Performance Guarantee */}
                <div className="border-b border-gray-100 p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <IconShield size={16} className="text-blue-600" />
                    <h4 className="text-sm font-medium text-gray-700">
                      {t("accountReceivable.performanceGuarantee") || "履約保證"}
                    </h4>
                    {performanceBond.isRetrieved && (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        {t("accountReceivable.retrieved") || "已取回"}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconHash size={13} />
                        {t("accountReceivable.voucherNumber") || "票號"}
                      </label>
                      <Input
                        value={performanceBond.checkNumber}
                        onChange={(e) => setPerformanceBond((prev) => ({ ...prev, checkNumber: e.target.value }))}
                        placeholder={t("accountReceivable.enterVoucherNumber") || "請輸入票號"}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCalendar size={13} />
                        {t("accountReceivable.retrievalDate") || "取回日期"}
                      </label>
                      <DatePicker
                        style={{ width: "100%" }}
                        value={performanceBond.retrievalDate ? dayjs(performanceBond.retrievalDate) : null}
                        onChange={(e) => setPerformanceBond((prev) => ({ ...prev, retrievalDate: e ? e.format("YYYY-MM-DD") : null }))}
                      />
                    </div>
                    <div className="flex items-end pb-1 lg:col-span-2">
                      <Checkbox
                        checked={performanceBond.isRetrieved}
                        onChange={(e) => setPerformanceBond((prev) => ({ ...prev, isRetrieved: e.currentTarget.checked }))}
                        label={
                          <span className="flex items-center gap-1.5">
                            <IconShieldCheck size={14} className="text-green-600" />
                            {t("accountReceivable.retrievedGuarantee") || "已取回履約保證"}
                          </span>
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Construction Insurance */}
                <div className="border-b border-gray-100 p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <IconShieldCheck size={16} className="text-blue-600" />
                    <h4 className="text-sm font-medium text-gray-700">
                      {t("accountReceivable.constructionInsurance") || "工程保險"}
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCalendar size={13} />
                        {t("accountReceivable.startDate") || "起始日期"}
                      </label>
                      <DatePicker
                        style={{ width: "100%" }}
                        value={engineeringInsurance.startDate ? dayjs(engineeringInsurance.startDate) : null}
                        onChange={(e) => setEngineeringInsurance((prev) => ({ ...prev, startDate: e ? e.format("YYYY-MM-DD") : null }))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCalendar size={13} />
                        {t("accountReceivable.expirationDate") || "到期日期"}
                      </label>
                      <DatePicker
                        style={{ width: "100%" }}
                        value={engineeringInsurance.expiryDate ? dayjs(engineeringInsurance.expiryDate) : null}
                        onChange={(e) => setEngineeringInsurance((prev) => ({ ...prev, expiryDate: e ? e.format("YYYY-MM-DD") : null }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Warranty Bond */}
                <div className="p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <IconTicket size={16} className="text-blue-600" />
                    <h4 className="text-sm font-medium text-gray-700">
                      {t("accountReceivable.warrantyBond") || "保固本票"}
                    </h4>
                    {warrantyBond.isRetrieved && (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        {t("accountReceivable.retrieved") || "已取回"}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconHash size={13} />
                        {t("accountReceivable.voucherNumber") || "票號"}
                      </label>
                      <Input
                        value={warrantyBond.checkNumber}
                        onChange={(e) => setWarrantyBond((prev) => ({ ...prev, checkNumber: e.target.value }))}
                        placeholder={t("accountReceivable.enterVoucherNumber") || "請輸入票號"}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCalendar size={13} />
                        {t("accountReceivable.retrievalDate") || "取回日期"}
                      </label>
                      <DatePicker
                        style={{ width: "100%" }}
                        value={warrantyBond.retrievalDate ? dayjs(warrantyBond.retrievalDate) : null}
                        onChange={(e) => setWarrantyBond((prev) => ({ ...prev, retrievalDate: e ? e.format("YYYY-MM-DD") : null }))}
                      />
                    </div>
                    <div className="flex items-end pb-1">
                      <Checkbox
                        checked={warrantyBond.isRetrieved}
                        onChange={(e) => setWarrantyBond((prev) => ({ ...prev, isRetrieved: e.currentTarget.checked }))}
                        label={
                          <span className="flex items-center gap-1.5">
                            <IconShieldCheck size={14} className="text-green-600" />
                            {t("accountReceivable.retrievedWarrantyBond") || "已取回保固本票"}
                          </span>
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Collection Records */}
          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex items-center gap-2">
                <IconReceipt size={16} className="text-blue-600" />
                <h3 className="text-sm font-medium text-gray-700">
                  {t("accountReceivable.collectionRecord") || "收款紀錄"}
                </h3>
                <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-500">
                  {tempReceivableList.length} {t("accountReceivable.records") || "筆"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{t("accountReceivable.filterByDate") || "篩選收款日期"}</span>
                <DatePicker.RangePicker
                  size="small"
                  value={exportDateRange}
                  onChange={(dates) => setExportDateRange(dates)}
                  placeholder={[t("accountReceivable.startDate") || "開始日期", t("accountReceivable.endDate") || "結束日期"]}
                />
                <Button
                  variant="default"
                  radius="md"
                  size="sm"
                  leftSection={<IconFileExport size={14} />}
                  onClick={handleExportModalRecords}
                >
                  {t("accountReceivable.exportExcel") || "匯出Excel"}
                </Button>
                <Button
                  variant="default"
                  radius="md"
                  size="sm"
                  leftSection={<IconPlus size={14} />}
                  onClick={() =>
                    setTempReceivableList((prev) => [
                      ...prev,
                      { paymentDate: null, amount: "", bankId: "", paymentMethod: "transfer", remarks: "", key: uuidv4() },
                    ])
                  }
                >
                  {t("accountReceivable.addCollectionRecord") || "增加收款紀錄"}
                </Button>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {tempReceivableList.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <IconReceipt size={48} className="mb-3 text-gray-300" />
                  <p className="text-sm font-medium text-gray-400">
                    {t("accountReceivable.noCollectionData") || "無收款資料"}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">{t("accountReceivable.clickToAddRecord") || "點擊上方按鈕新增收款紀錄"}</p>
                </div>
              )}

              {tempReceivableList.map((record, index) => (
                <div
                  key={record.id || record.key}
                  className="p-6"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-600">
                      {t("accountReceivable.collectionRecordLabel") || "收款紀錄 #"}{index + 1}
                    </span>
                    <Button
                      variant="subtle"
                      color="red"
                      size="sm"
                      radius="md"
                      leftSection={<IconTrash size={14} />}
                      onClick={() => {
                        setTempReceivableList((prev) =>
                          prev.filter((r) => r?.key !== record?.key)
                        );
                      }}
                    >
                      {t("accountReceivable.delete") || "刪除"}
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCalendar size={13} />
                        {t("accountReceivable.collectionDate") || "收款日期"}＊
                      </label>
                      <DatePicker
                        style={{ width: "100%" }}
                        value={record?.paymentDate ? dayjs(record?.paymentDate) : null}
                        onChange={(e) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            e ? e.format("YYYY-MM-DD") : null,
                            "paymentDate"
                          )
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCashBanknote size={13} />
                        {t("accountReceivable.collectionAmount") || "收款金額"}＊
                      </label>
                      <InputNumber
                        style={{ width: "100%" }}
                        value={record?.amount}
                        onChange={(val) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            val,
                            "amount"
                          )
                        }
                        formatter={numberFormatter}
                        parser={numberParser}
                        step={0.01}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconBuilding size={13} />
                        {t("accountReceivable.bank") || "銀行"}＊
                      </label>
                      <AutoComplete
                        style={{ width: "100%" }}
                        value={
                          getBankById(record?.bankId)
                            ? `${getBankById(record?.bankId).name} (ID: ${record?.bankId})`
                            : record?.bankId || ""
                        }
                        options={bankData?.map((b) => ({
                          label: b?.name + ` (ID: ${b?.id})`,
                          value: b?.name + ` (ID: ${b?.id})`,
                        }))}
                        onChange={(value) => {
                          let match = value.match(/ID:\s*([a-zA-Z0-9-]+)/);
                          let idNumber = match ? match[1] : null;
                          if (idNumber) {
                            handleChangeReceivableDetail(
                              record?.id || record?.key,
                              idNumber,
                              "bankId"
                            );
                          }
                        }}
                        placeholder={t("bank.enterBankName") || "請輸入銀行名稱"}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCashBanknote size={13} />
                        {t("accountReceivable.paymentMethod") || "付款方式"}＊
                      </label>
                      <Select
                        comboboxProps={{ zIndex: 1001 }}
                        value={record?.paymentMethod || "transfer"}
                        data={[
                          { value: "transfer", label: t("accountReceivable.transfer") || "匯款" },
                          { value: "check", label: t("accountReceivable.check") || "支票" },
                          { value: "cash", label: t("accountReceivable.cash") || "現金" },
                          { value: "credit", label: t("accountReceivable.credit") || "信用卡" },
                        ]}
                        onChange={(e) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            e,
                            "paymentMethod"
                          )
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconHash size={13} />
                        {t("accountReceivable.invoiceNumber") || "發票號碼"}＊
                      </label>
                      <Input
                        value={record?.invoiceNumber}
                        onChange={(e) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            e.target.value,
                            "invoiceNumber"
                          )
                        }
                        placeholder="INV-XXXX-XXX"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCalendar size={13} />
                        {t("accountReceivable.invoiceDate") || "發票日期"}＊
                      </label>
                      <DatePicker
                        style={{ width: "100%" }}
                        value={record?.invoiceDate ? dayjs(record?.invoiceDate) : null}
                        onChange={(e) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            e ? e.format("YYYY-MM-DD") : null,
                            "invoiceDate"
                          )
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconHash size={13} />
                        {t("accountReceivable.checkNumber") || "支票號碼"}
                      </label>
                      <Input
                        value={record?.checkNumber || ""}
                        onChange={(e) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            e.target.value,
                            "checkNumber"
                          )
                        }
                        placeholder="CHK-XXXXX"
                        disabled={record?.paymentMethod !== "check"}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCalendar size={13} />
                        {t("accountReceivable.checkClearanceDate") || "兌現日期"}
                      </label>
                      <DatePicker
                        style={{ width: "100%" }}
                        value={record?.checkClearanceDate ? dayjs(record?.checkClearanceDate) : null}
                        disabled={record?.paymentMethod !== "check"}
                        onChange={(e) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            e ? e.format("YYYY-MM-DD") : null,
                            "checkClearanceDate"
                          )
                        }
                      />
                    </div>

                    <div className="col-span-full my-1 border-t border-gray-100" />

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconPercentage size={13} />
                        {t("accountReceivable.handlingFee") || "手續費"}
                      </label>
                      <InputNumber
                        style={{ width: "100%" }}
                        value={record?.handlingFee ?? 0}
                        onChange={(val) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            val,
                            "handlingFee"
                          )
                        }
                        formatter={numberFormatter}
                        parser={numberParser}
                        step={0.01}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconWallet size={13} />
                        {t("accountReceivable.retentionAmount") || "保留款"}
                      </label>
                      <InputNumber
                        style={{ width: "100%" }}
                        value={record?.retentionAmount ?? 0}
                        onChange={(val) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            val,
                            "retentionAmount"
                          )
                        }
                        formatter={numberFormatter}
                        parser={numberParser}
                        step={0.01}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconCalculator size={13} />
                        {t("accountReceivable.deductionAmount") || "扣款金額"}
                      </label>
                      <InputNumber
                        style={{ width: "100%" }}
                        value={record?.deductionAmount ?? 0}
                        onChange={(val) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            val,
                            "deductionAmount"
                          )
                        }
                        formatter={numberFormatter}
                        parser={numberParser}
                        step={0.01}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs text-gray-500">
                        {t("accountReceivable.netAmount") || "實收金額"}
                      </label>
                      <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3">
                        <span className={`font-semibold ${record?.isSettled ? "text-green-600" : "text-gray-900"}`}>
                          {formatDecimal(Number(record?.amount || 0) - (record?.retentionAmount || 0))}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-end pb-1">
                      <Checkbox
                        checked={record?.isSettled || false}
                        onChange={(e) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            e.currentTarget.checked,
                            "isSettled"
                          )
                        }
                        label={t("accountReceivable.isSettled") || "已結清"}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <IconMessage size={13} />
                        {t("accountReceivable.remarks") || "備註"}
                      </label>
                      <Textarea
                        value={record?.remarks}
                        onChange={(e) =>
                          handleChangeReceivableDetail(
                            record?.id || record?.key,
                            e.target.value,
                            "remarks"
                          )
                        }
                        placeholder={t("accountReceivable.enterRemarks") || "備註"}
                        minRows={1}
                        autosize
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AccountReceivableMainPage;
